import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { brandService, Brand } from '../../services/brandService';
import toast from 'react-hot-toast';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Tab } from '@headlessui/react';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

interface BrandDetailModalProps {
    isOpen: boolean;
    brandId: number;
    onClose: () => void;
    onUpdate: () => void;
}

export const BrandDetailModal: React.FC<BrandDetailModalProps> = ({ isOpen, brandId, onClose, onUpdate }) => {
    const [brand, setBrand] = useState<Brand | null>(null);
    const [loading, setLoading] = useState(true);

    // Form states
    const [newColor, setNewColor] = useState('');
    const [newSize, setNewSize] = useState('');
    const [addingColor, setAddingColor] = useState(false);
    const [addingSize, setAddingSize] = useState(false);

    const loadBrand = async () => {
        setLoading(true);
        try {
            const result = await brandService.getBrandById(brandId);
            setBrand(result.data);
        } catch (error) {
            toast.error('Error cargando detalles');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && brandId) {
            loadBrand();
        }
    }, [isOpen, brandId]);

    const handleAddColor = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newColor.trim()) return;
        setAddingColor(true);
        try {
            await brandService.addColor(brandId, { name: newColor });
            setNewColor('');
            toast.success('Color agregado');
            loadBrand();
            onUpdate();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error agregando color');
        } finally {
            setAddingColor(false);
        }
    };

    const handleDeleteColor = async (colorId: number) => {
        if (!confirm('¿Eliminar color?')) return;
        try {
            await brandService.deleteColor(brandId, colorId); // brandId passed for context if needed
            toast.success('Color eliminado');
            loadBrand();
            onUpdate();
        } catch (error: any) {
            toast.error('Error eliminando color');
        }
    };

    const handleAddSize = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newSize.trim()) return;
        setAddingSize(true);
        try {
            await brandService.addSize(brandId, { name: newSize });
            setNewSize('');
            toast.success('Talla agregada');
            loadBrand();
            onUpdate();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error agregando talla');
        } finally {
            setAddingSize(false);
        }
    };

    const handleDeleteSize = async (sizeId: number) => {
        if (!confirm('¿Eliminar talla?')) return;
        try {
            await brandService.deleteSize(brandId, sizeId);
            toast.success('Talla eliminada');
            loadBrand();
            onUpdate();
        } catch (error: any) {
            toast.error('Error eliminando talla');
        }
    };

    const handleUpdateBrand = async (data: Partial<Brand>) => {
        try {
            await brandService.updateBrand(brandId, data);
            toast.success('Marca actualizada');
            loadBrand();
            onUpdate();
        } catch (error) {
            toast.error('Error actualizando marca');
        }
    };

    if (!brand && loading) return <Modal isOpen={isOpen} onClose={onClose}><div className="p-4 text-center">Cargando...</div></Modal>;
    if (!brand) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Administrar: ${brand.name}`} size="xl">
            <div className="flex flex-col h-[60vh]">
                <div className="bg-gray-50 p-4 rounded-lg mb-4 flex justify-between items-center border border-gray-100">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Información General</h4>
                        <div className="flex items-center gap-4 mt-1">
                            <div className="text-lg font-bold text-gray-900">{brand.name}</div>
                            <span className="text-xs font-mono bg-gray-200 px-2 py-0.5 rounded">{brand.code}</span>
                            <span className={`px-2 text-xs rounded-full ${brand.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {brand.active ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>
                    </div>
                    {/* Edit basic info could go here, for now just toggle active */}
                    <button
                        onClick={() => handleUpdateBrand({ active: !brand.active })}
                        className="text-blue-600 text-sm hover:underline"
                    >
                        {brand.active ? 'Desactivar' : 'Activar'}
                    </button>
                </div>

                <Tab.Group>
                    <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
                        {['Colores', 'Tallas'].map((category) => (
                            <Tab
                                key={category}
                                className={({ selected }) =>
                                    classNames(
                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700',
                                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                        selected
                                            ? 'bg-white shadow'
                                            : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                                    )
                                }
                            >
                                {category}
                            </Tab>
                        ))}
                    </Tab.List>
                    <Tab.Panels className="mt-2 flex-1 overflow-y-auto">
                        {/* COLORS PANEL */}
                        <Tab.Panel className={classNames('rounded-xl bg-white p-3', 'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2')}>
                            <form onSubmit={handleAddColor} className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="Nuevo Color (Ej. ROJO)"
                                    value={newColor}
                                    onChange={e => setNewColor(e.target.value.toUpperCase())}
                                />
                                <button
                                    type="submit"
                                    disabled={addingColor || !newColor.trim()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                </button>
                            </form>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {brand.colors?.map(color => (
                                    <div key={color.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 group">
                                        <span className="text-sm font-medium text-gray-700">{color.name}</span>
                                        <button
                                            onClick={() => handleDeleteColor(color.id)}
                                            className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {brand.colors?.length === 0 && <p className="text-gray-400 text-sm col-span-3 text-center py-4">Sin colores registrados</p>}
                            </div>
                        </Tab.Panel>

                        {/* SIZES PANEL */}
                        <Tab.Panel className={classNames('rounded-xl bg-white p-3', 'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2')}>
                            <form onSubmit={handleAddSize} className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="Nueva Talla (Ej. M)"
                                    value={newSize}
                                    onChange={e => setNewSize(e.target.value.toUpperCase())}
                                />
                                <button
                                    type="submit"
                                    disabled={addingSize || !newSize.trim()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                </button>
                            </form>

                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {brand.sizes?.map(size => (
                                    <div key={size.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 group">
                                        <span className="text-sm font-medium text-gray-700">{size.name}</span>
                                        <button
                                            onClick={() => handleDeleteSize(size.id)}
                                            className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {brand.sizes?.length === 0 && <p className="text-gray-400 text-sm col-span-4 text-center py-4">Sin tallas registradas</p>}
                            </div>
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>

                <div className="mt-auto pt-4 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
};
